import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

type FilterState = any;
type PanelState = any;

export interface SubpanelState {
  component: any;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class PanelStateService {
  private readonly filterStateSubject = new BehaviorSubject<FilterState>(null);
  private readonly panelStateSubject = new BehaviorSubject<PanelState>(null);
  private readonly subpanelStateSubject = new BehaviorSubject<SubpanelState | null>(null);

  public readonly filterState$: Observable<FilterState> = this.filterStateSubject.asObservable();
  public readonly panelState$: Observable<PanelState> = this.panelStateSubject.asObservable();
  public readonly subpanelState$: Observable<SubpanelState | null> = this.subpanelStateSubject.asObservable();

  //#region --- Setters ---
  updateFilterState(filter: FilterState): void {
    this.filterStateSubject.next(filter);
  }

  updatePanelState(panel: PanelState): void {
    this.panelStateSubject.next(panel);
  }

  setSubpanelState(subpanel: SubpanelState | null): void {
    this.subpanelStateSubject.next(subpanel);
  }

  updateSubpanelData(newData: Partial<SubpanelState['data']>): void {
    const currentSubpanel = this.subpanelStateSubject.getValue();
    if (currentSubpanel) {
      if (newData === null) {
        this.subpanelStateSubject.next({
          ...currentSubpanel,
          data: null
        });
      }
      else
        this.subpanelStateSubject.next({
          ...currentSubpanel,
          data: { ...currentSubpanel.data, ...newData }
        });
    } else {
      // If no subpanel exists, initialize one with a null component and data.
      this.subpanelStateSubject.next({ component: null, data: null });
    }
  }

  //#region --- Getters ---
  get currentFilterState(): FilterState {
    return this.filterStateSubject.getValue();
  }

  get currentPanelState(): PanelState {
    return this.panelStateSubject.getValue();
  }

  get currentSubpanelState(): SubpanelState | null {
    return this.subpanelStateSubject.getValue();
  }

  //#region --- Subscription Helper Methods ---
  subscribeToFilterState(
    next: (filter: FilterState) => void,
    error?: (error: any) => void,
    complete?: () => void
  ): Subscription {
    return this.filterState$.subscribe({ next, error, complete });
  }

  subscribeToPanelState(
    next: (panel: PanelState) => void,
    error?: (error: any) => void,
    complete?: () => void
  ): Subscription {
    return this.panelState$.subscribe({ next, error, complete });
  }

  subscribeToSubpanelState(
    next: (subpanel: SubpanelState | null) => void,
    error?: (error: any) => void,
    complete?: () => void
  ): Subscription {
    return this.subpanelState$.subscribe({ next, error, complete });
  }
}
